import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import CannedFeedback from '../CannedFeedback';
import FeedbackIconSet from './FeedbackIconSet';
import * as Constants from '../../constants/constants';
import './styles.css';
import '../../global/typography.css';

const Feedback = (props) => {
  const {
    allowUserSelection,
    numberOfStars,
    titleStyle,
    starSize,
    onRatingSelect,
    onClick,
    feedbackReaction,
    feedbackListOptions,
    onFeedbackSubmit,
    getLocaleStr,
    title,
    isCA,
  } = props;

  const starsMap = [...Array(numberOfStars)];
  const [starRating, setStarRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackListSelections, setFeedbackListSelections] = useState([]);
  const [feedbackText, setFeedbackText] = useState('');

  const onMouseEnter = (index) => setHoverRating(index);

  const onMouseLeave = () => setHoverRating(0);

  const toggleFeedbackListSelections = (listId) => {
    const selectedListSet = new Set(feedbackListSelections);
    if (selectedListSet.has(listId)) selectedListSet.delete(listId);
    else selectedListSet.add(listId);
    const newSelections = Array.from(selectedListSet);
    setFeedbackListSelections(newSelections);
  };

  const onFeedbackRatingSelection = (rating) => {
    setStarRating(rating);
    onRatingSelect(rating);
  };

  const onFeedbackTextInput = (e) => setFeedbackText(e.target.value);

  const _onFeedbackSubmit = () => {
    if (starRating) {
      const cannedResponses = feedbackListOptions
        .filter((option) => feedbackListSelections.includes(option.id))
        .map((option) => option.title);
      onFeedbackSubmit(starRating, cannedResponses, feedbackText);
    }
  };

  useEffect(() => {
    setFeedbackListSelections([]);
  }, [feedbackListOptions]);

  const feedbackMaxLengthCSS = 'roboto typography-regular font-400 fb-max-length '.concat(feedbackText.length === Constants.FEEDBACK_MAX_LENGTH ? 'red' : 'gray');
  const feedbackMaxLengthCSSCA = 'roboto typography-regular font-400 fb-max-length-ca '.concat(feedbackText.length === Constants.FEEDBACK_MAX_LENGTH ? 'red' : 'gray');
  const feedbackLengthRemaining = feedbackText.length > Constants.FEEDBACK_MAX_LENGTH ? 0 : (Constants.FEEDBACK_MAX_LENGTH - feedbackText.length);

  const renderStars = () => (
    <>
      <span className={`feedback-title ${titleStyle}`}>{title}</span>
      <div className={isCA ? 'feedback-stars-ca' : 'feedback-stars'}>
        {starsMap.map((_, index) => {
          const i = index + 1;
          return (
            <FeedbackIconSet
              key={`star_${i}`}
              index={i}
              rating={starRating}
              starSize={starSize}
              hoverRating={hoverRating}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onRatingSelection={onFeedbackRatingSelection}
              allowUserSelection={allowUserSelection}
              isCA={isCA}
            />
          );
        })}
      </div>
    </>
  );

  const renderStars_ca = () => (
    <>
      <span className={`feedback-title-ca ${titleStyle}`}>{title}</span>
      <div className="feedback-stars">
        {starsMap.map((_, index) => {
          const i = index + 1;
          return (
            <FeedbackIconSet
              key={`star_${i}`}
              index={i}
              rating={starRating}
              starSize={starSize}
              hoverRating={hoverRating}
              onMouseEnter={onMouseEnter}
              onMouseLeave={onMouseLeave}
              onRatingSelection={onFeedbackRatingSelection}
              allowUserSelection={allowUserSelection}
              isCA={isCA}
            />
          );
        })}
      </div>
    </>
  );

  return (
    <>
      { allowUserSelection
        ? (
          <>
            <div className={isCA ? 'feedback-ca' : 'feedback'}>
              {isCA ? renderStars_ca() : renderStars()}
            </div>
            {!isCA && <span className="roboto feedback-reaction typography-regular font-500">{feedbackReaction}</span>}
            <CannedFeedback
              list={feedbackListOptions}
              selectedOptions={feedbackListSelections}
              toggleListSelection={toggleFeedbackListSelections}
              isCA={isCA}
            />
            <div id="feedback-text" className="feedback-input">
              {isCA && <span className="roboto text-area-title typography-regular font-500">{getLocaleStr('add_more_details')}</span>}
              <textarea
                data-testid="feedback-text"
                value={feedbackText}
                onChange={onFeedbackTextInput}
                className="roboto typography-regular feedback-input-textarea font-400"
                rows="15"
                maxLength={Constants.FEEDBACK_MAX_LENGTH}
                placeholder={!isCA && getLocaleStr('tell_us_what_you_liked')}
              />
              <span className={isCA ? feedbackMaxLengthCSSCA : feedbackMaxLengthCSS}>{`${feedbackLengthRemaining} characters left`}</span>
            </div>
            <button
              type="button"
              onClick={_onFeedbackSubmit}
              className={`roboto font-500 typography-regular ${isCA ? 'feedback-send-ca' : 'feedback-send'}`}
            >
              {getLocaleStr('buttons.send')}
            </button>
          </>
        )
        : (
          <>
            <button type="button" className={isCA ? 'feedback-ca no-style-button' : 'feedback no-style-button'} onClick={onClick}>
              {isCA ? renderStars_ca() : renderStars()}
            </button>
            {isCA && (
            <CannedFeedback
              list={feedbackListOptions}
              selectedOptions={feedbackListSelections}
              toggleListSelection={toggleFeedbackListSelections}
              isCA={isCA}
            />
            )}
          </>
        )}
    </>
  );
};

Feedback.propTypes = {
  allowUserSelection: PropTypes.bool,
  numberOfStars: PropTypes.number,
  titleStyle: PropTypes.string,
  starSize: PropTypes.string,
  onRatingSelect: PropTypes.func,
  onClick: PropTypes.func,
  feedbackReaction: PropTypes.string,
  feedbackListOptions: PropTypes.instanceOf(Array),
  onFeedbackSubmit: PropTypes.func,
  getLocaleStr: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  isCA: PropTypes.bool,
};

Feedback.defaultProps = {
  allowUserSelection: false,
  numberOfStars: 5,
  titleStyle: '',
  starSize: '',
  onRatingSelect: () => { },
  onClick: () => { },
  onFeedbackSubmit: () => { },
  feedbackReaction: '',
  feedbackListOptions: [],
  isCA: false,
};

export default Feedback;
